import { faWindowMinimize } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';

import LogoImage from 'src/assets/images/branding/enosi-logo.svg';
import Logo from 'src/enosikit/components/Logo';

/**
 * NavigationLogo - Displays primary brand logo with optional cobrand logo
 *
 * ⚠️ IMPORTANT: This component MUST ALWAYS be rendered unconditionally.
 *
 * DO NOT wrap this component in a conditional check like:
 * ❌ WRONG: {cobrand && <NavigationLogo cobrand={cobrand} primaryBrand={primaryBrand} />}
 * ✅ CORRECT: <NavigationLogo cobrand={cobrand} primaryBrand={primaryBrand} />
 *
 * RATIONALE:
 * - The component is designed to handle null/undefined cobrand values gracefully
 * - It will always display the primary brand logo (Enosi)
 * - The cobrand logo is only shown when available (handled internally)
 * - Conditional rendering prevents the primary logo from appearing when cobrand is missing
 *
 * BEHAVIOR:
 * - Always renders the primary brand logo
 * - Conditionally renders cobrand logo with separator if cobrand prop is provided
 * - Falls back to default primary brand if primaryBrand prop is not provided
 * @param {object} props - Component props
 * @param {object|null} [props.cobrand=null] - Optional cobrand configuration object.
 *   When null/undefined, only the primary brand is displayed.
 * @param {object} [props.primaryBrand] - Primary brand configuration (defaults to Enosi branding).
 * @returns {React.ReactElement} NavigationLogo component - always renders, never returns null
 */
function NavigationLogo({
  primaryBrand = {
    imagePath: LogoImage,
    title: 'Enosi Powertracer',
    type: 'BRAND_PRIMARY',
  }, cobrand = null,
}) {
  const renderCobrand = (cobrandParam) => {
    if (cobrandParam === undefined) { return (null); }
    if (cobrandParam === null) { return (null); }

    return (
      <>
        <FontAwesomeIcon icon={faWindowMinimize} size="1x" className="ms-1" color="rgb(0,0,0,0.55)" rotation={90} style={{ marginRight: '-.375em' }} />
        <Logo image={cobrandParam.imagePath} name={cobrandParam.title} height="27px" />
      </>
    );
  };

  return (
    <>
      <Logo image={primaryBrand.imagePath} name={primaryBrand.title} />
      {renderCobrand(cobrand)}
    </>
  );
}

NavigationLogo.propTypes = {
  /**
   * OPTIONAL: Cobrand configuration. Can be null/undefined.
   * Component handles null gracefully - DO NOT conditionally render this component
   * based on cobrand availability.
   */
  cobrand: PropTypes.oneOfType([
    PropTypes.shape({
      imagePath: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    PropTypes.oneOf([null, undefined]),
  ]),
  /**
   * Primary brand configuration. Defaults to Enosi branding if not provided.
   */
  primaryBrand: PropTypes.shape({
    imagePath: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
};

export default NavigationLogo;
